/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import DynamicFieldsModel from '@linc-technologies/linced-common/models/dynamic-field/base-model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr, belongsTo, type AsyncBelongsTo } from '@warp-drive/legacy/model';
import { fragment } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type EnrolmentStatus from '@linc-technologies/linced-common/enums/enrolment-status';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import NzMoeSchool from '@linc-technologies/linced-common/enums/nz-moe-school';
import type PostCounts from '@linc-technologies/linced-common/models/people/post-counts';
import NzMoeDepartureReason from 'linced-app/enums/nz-moe-departure-reason';
import NzMoePostSchoolActivity from 'linced-app/enums/nz-moe-post-school-activity';
import type Application from 'linced-app/models/enrolments/application';
import type Media from 'linced-app/models/media';
import type EnrolmentMeta from 'linced-app/models/people/enrolment-meta';
import type Learner from 'linced-app/models/people/learner';

// Proto: people.Enrolment.
export default class Enrolment extends DynamicFieldsModel {
  declare [Type]: 'people/enrolment';

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  // personId is the linked Hero person ID.
  // This is NOT a relationship, as we need to be able to set it on create without a Person record if the learner is
  // already in Hero (but not currently associated with this school).
  @attr('string', { defaultValue: '' })
  declare personId: string;

  @belongsTo('media', { async: true, inverse: null })
  declare profileMediaId: AsyncBelongsTo<Media>;

  @alias('profileMediaId')
  declare profileMedia: AsyncBelongsTo<Media>;

  @belongsTo('enrolments/application', { async: true, inverse: 'enrolment' })
  declare applicationId: AsyncBelongsTo<Application>;

  @alias('applicationId')
  declare application: AsyncBelongsTo<Application>;

  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  @attr('string', { defaultValue: '' })
  declare jurisdictionalId: string;

  @attr('number', { defaultValue: 0 })
  declare jurisdictionalStatus: number;

  @attr('number', { defaultValue: 0 })
  declare status: EnumValue<typeof EnrolmentStatus> | 0;

  @attr('number', { defaultValue: 0 })
  declare fte: number;

  @attr('string')
  declare entryDate: string | undefined;

  @attr('string')
  declare exitDate: string | undefined;

  @attr('string', { defaultValue: '' })
  declare departureReason: string;

  @computed('jurisdiction', 'departureReason')
  get departureReasonLabel() {
    if (isEmpty(this.departureReason)) {
      return '';
    }

    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        return NzMoeDepartureReason.getLabel(this.departureReason);
      default:
        return this.departureReason;
    }
  }

  @fragment('people/learner')
  declare learner: Learner | null;

  @fragment('people/post-counts', { defaultValue: () => ({}) })
  declare postCounts: PostCounts | null;

  // meta contains a field-value map for storing jurisdictional information relating to this Enrolment.
  @fragment('people/enrolment-meta', { defaultValue: () => ({}) })
  declare meta: EnrolmentMeta | null;

  @computed('jurisdiction', 'meta.fields.nzMOE:destinationSchool.value')
  get destinationSchoolName() {
    let field = 'meta.fields.international:destinationSchool.value';
    if (this.jurisdiction === Jurisdiction.NZ_MOE) {
      field = 'meta.fields.nzMOE:destinationSchool.value';
    }

    const val = this.get(field);
    if (isEmpty(val)) {
      return '';
    }

    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        return NzMoeSchool.getLabel(val);
      default:
        return val;
    }
  }

  @computed('jurisdiction', 'meta.fields.nzMOE:postSchoolActivity.value')
  get postSchoolActivityLabel() {
    const val = this.get('meta.fields.nzMOE:postSchoolActivity.value');
    if (isEmpty(val)) {
      return '';
    }

    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        return NzMoePostSchoolActivity.getLabel(val);
      default:
        return '';
    }
  }
}
